import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { MOBILE_BREAKPOINT } from "./breakpoints";
import SelectDropdown from "./SelectDropdown";

const StyledFooter = styled.nav({
    width: "100%",
    minHeight: "68px",
    padding: tokens.globals["spacing-md"],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.globals["spacing-md"],
    borderTop: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        alignItems: "flex-start",
        flexDirection: "column",
    },
});

const StyledAmountText = styled.span({
    color: tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
});

const StyledRight = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.globals["spacing-8"],
    flexWrap: "wrap",

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
        justifyContent: "space-between",
        gap: tokens.globals["spacing-3"],
    },
});

const StyledRowsPerPage = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-sm"],
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

const StyledRowsPerPageSelect = styled.div({
    width: "80px",
});

const StyledPageText = styled.span({
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

const StyledControls = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-sm"],
});

const StyledPageButton = styled.button({
    boxSizing: "border-box",
    width: tokens.globals["spacing-8"],
    height: tokens.globals["spacing-8"],
    padding: 0,
    background: tokens.colors.surfaces["container-background"],
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colors.text.primary,
    cursor: "pointer",

    "&:hover:not(:disabled)": {
        borderColor: tokens.colors.brand.blue["500"],
        color: tokens.colors.brand.blue["500"],
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-px"],
    },

    "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.5,
    },
});

/**
 * Standard AIE table pagination footer: entry-count summary, rows-per-page
 * select, and first/prev/next/last page controls.
 * @param {number} [props.currentPage] Current page (1-based), clamped to `totalPages`.
 * @param {(page: number) => void} [props.onPageChange] Called with the already-clamped target page.
 * @param {boolean} [props.disabled] Disables all controls, e.g. during a running bulk/process action.
 * @param {string} [props.disabledReason] Tooltip shown while disabled.
 * Also: totalEntries, rangeStart, rangeEnd, rowsPerPage, rowsPerPageOptions, onRowsPerPageChange,
 *   totalPages, className (self-explanatory).
 */
function Pagination({
    totalEntries = 0,
    rangeStart = 0,
    rangeEnd = 0,
    rowsPerPage = 10,
    rowsPerPageOptions = [10, 25, 50, 100],
    onRowsPerPageChange,
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    disabled = false,
    disabledReason,
    className,
}) {
    const safeTotalPages = Math.max(1, totalPages || 1);
    const safeCurrentPage = Math.min(Math.max(1, currentPage || 1), safeTotalPages);
    const atFirst = safeCurrentPage <= 1;
    const atLast = safeCurrentPage >= safeTotalPages;
    const hasEntries = totalEntries > 0;
    const displayStart = hasEntries ? Math.max(1, rangeStart || 1) : 0;
    const displayEnd = hasEntries ? Math.min(rangeEnd || totalEntries, totalEntries) : 0;

    const changePage = (page) => {
        if (disabled || !onPageChange) {
            return;
        }
        onPageChange(Math.min(Math.max(1, page), safeTotalPages));
    };

    return (
        <StyledFooter
            className={className}
            aria-label="Table pagination"
            aria-disabled={disabled || undefined}
            title={disabled ? disabledReason : undefined}
        >
            <StyledAmountText>
                Showing {displayStart} to {displayEnd} of {totalEntries} entries
            </StyledAmountText>

            <StyledRight>
                <StyledRowsPerPage>
                    Rows per page
                    <StyledRowsPerPageSelect>
                        <SelectDropdown
                            size="md"
                            value={rowsPerPage}
                            onChange={(event) => onRowsPerPageChange && onRowsPerPageChange(Number(event.target.value), event)}
                            options={rowsPerPageOptions.map((option) => ({ value: String(option), label: String(option) }))}
                            disabled={disabled || !hasEntries}
                            disabledReason={disabledReason}
                            ariaLabel="Rows per page"
                        />
                    </StyledRowsPerPageSelect>
                </StyledRowsPerPage>

                <StyledPageText>
                    Page {safeCurrentPage} of {safeTotalPages}
                </StyledPageText>

                <StyledControls>
                    <StyledPageButton
                        type="button"
                        onClick={() => changePage(1)}
                        disabled={disabled || atFirst}
                        aria-label="First page"
                    >
                        <i className="fal fa-chevrons-left" aria-hidden="true" />
                    </StyledPageButton>
                    <StyledPageButton
                        type="button"
                        onClick={() => changePage(safeCurrentPage - 1)}
                        disabled={disabled || atFirst}
                        aria-label="Previous page"
                    >
                        <i className="fal fa-chevron-left" aria-hidden="true" />
                    </StyledPageButton>
                    <StyledPageButton
                        type="button"
                        onClick={() => changePage(safeCurrentPage + 1)}
                        disabled={disabled || atLast}
                        aria-label="Next page"
                    >
                        <i className="fal fa-chevron-right" aria-hidden="true" />
                    </StyledPageButton>
                    <StyledPageButton
                        type="button"
                        onClick={() => changePage(safeTotalPages)}
                        disabled={disabled || atLast}
                        aria-label="Last page"
                    >
                        <i className="fal fa-chevrons-right" aria-hidden="true" />
                    </StyledPageButton>
                </StyledControls>
            </StyledRight>
        </StyledFooter>
    );
}

Pagination.displayName = "Pagination";

export default Pagination;
